import { useFormContext } from 'react-hook-form';
import { ClientAddress } from '@/app/(main)/client-detail/edit-main/components/ClientAdress';
import { BILLING_FIELDS, MAILING_TO_BILLING_MAP } from '@/app/(main)/clients/create/const/passenger';
import { getAddressPayload } from '@/app/(main)/clients/create/utils/getAddressPayload';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import { cn } from '@/utils/сn';

type MailingField = keyof typeof MAILING_TO_BILLING_MAP;

interface EditAddressFormProps {
  isRelated?: boolean;
}

export const EditAddressForm = ({ isRelated = false }: EditAddressFormProps) => {
  const { setValue, getValues } = useFormContext<CreateClientFormData>();
  const isBillingSameAsMailing = getValues('primary_passenger.client.is_billing_same_as_mailing');

  const handleSetBillingFromMailing = () => {
    const mailing = getValues('primary_passenger.client');
    const billingPayload: Record<string, string> = {};

    (Object.keys(MAILING_TO_BILLING_MAP) as MailingField[]).forEach((mailingField: MailingField) => {
      const billingField = MAILING_TO_BILLING_MAP[mailingField];
      billingPayload[billingField] = mailing?.[mailingField] ?? '';

      setValue(`primary_passenger.client.${billingField}`, mailing?.[mailingField] ?? '', {
        shouldDirty: true,
      });
    });
  };

  const onEmptyBillingAddress = () => {
    const billingPayload: Record<string, string> = {};
    BILLING_FIELDS.forEach((field) => {
      setValue(`primary_passenger.client.${field}`, '');
      billingPayload[field] = '';
    });
    setValue(`primary_passenger.client.is_billing_same_as_mailing`, false, {
      shouldDirty: true,
    });
  };

  const handleChangeSameAsPrimary = (checked: boolean) => {
    setValue(`primary_passenger.client.is_billing_same_as_mailing`, checked);

    if (checked) {
      handleSetBillingFromMailing();
    } else {
      onEmptyBillingAddress();
    }
  };

  const syncAddressToBilling = (mailingData: { [key: string]: string }) => {
    if (!isBillingSameAsMailing) return;

    const billingPayload: Record<string, string> = {};

    (Object.keys(mailingData) as MailingField[]).forEach((mailingField: MailingField) => {
      const billingField = MAILING_TO_BILLING_MAP[mailingField];
      const value = mailingData[mailingField] ?? '';
      billingPayload[billingField] = value;
      setValue(`primary_passenger.client.${billingField}`, value);
    });
  };

  const selectPlaceAddress = (address: { [key: string]: string }) => {
    const addressPayload = getAddressPayload(address);

    syncAddressToBilling(addressPayload);
  };

  const handleSaveOnBlur = (name: string, value: unknown) => {
    selectPlaceAddress({ [name]: value as string });
  };

  return (
    <div className={cn('pb-0', { 'pb-[30px]': !isBillingSameAsMailing })}>
      <div className="pb-5 max-md:pb-4">
        <p className="text-body-medium font-semibold text-black pb-4">Mailing Address</p>
        <ClientAddress
          selectPlaceAddress={selectPlaceAddress}
          onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
          fields={{
            address_line1: {
              label: 'Address Line 1',
              placeholder: 'Street address, P.O. box',
              name: 'primary_passenger.client.client_mailing_address',
            },
            address_line2: {
              label: 'Address Line 2',
              placeholder: 'Apartment, suite, unit, building',
              name: 'primary_passenger.client.client_mailing_address_line_2',
            },
            city: {
              label: 'City',
              placeholder: 'Enter city',
              name: 'primary_passenger.client.client_mailing_city',
            },
            state: {
              label: 'State/Province',
              placeholder: 'Select state',
              name: 'primary_passenger.client.client_mailing_state',
            },
            zip: {
              label: 'Zip Code',
              placeholder: 'Enter ZIP or postal code',
              name: 'primary_passenger.client.zip_code',
            },
            country: {
              label: 'Country',
              placeholder: 'Select country',
              name: 'primary_passenger.client.client_mailing_country',
            },
          }}
        />
      </div>
      {!isRelated && (
        <div className="pt-5 border-t border-grey-400 max-md:pt-4">
          <p className="text-body-medium font-semibold text-black pb-4">Billing Address</p>
          <ClientAddress
            toggleConfig={{
              label: 'Same as Mailing Address',
              visible: true,
              value: !!isBillingSameAsMailing,
              onChange: () => handleChangeSameAsPrimary(!isBillingSameAsMailing),
            }}
            fields={{
              address_line1: {
                label: 'Address Line 1',
                placeholder: 'Street address, P.O. box',
                name: 'primary_passenger.client.client_billing_address',
              },
              address_line2: {
                label: 'Address Line 2',
                placeholder: 'Apartment, suite, unit, building',
                name: 'primary_passenger.client.client_billing_address_line_2',
              },
              city: {
                label: 'City',
                placeholder: 'Enter city',
                name: 'primary_passenger.client.client_billing_city',
              },
              state: {
                label: 'State/Province',
                placeholder: 'Select state',
                name: 'primary_passenger.client.client_billing_state',
              },
              zip: {
                label: 'Zip Code',
                placeholder: 'Enter ZIP or postal code',
                name: 'primary_passenger.client.billing_zip_code',
              },
              country: {
                label: 'Country',
                placeholder: 'Select country',
                name: 'primary_passenger.client.client_billing_country',
              },
            }}
          />
        </div>
      )}
    </div>
  );
};
